/**
 * Die vier tool-eigenen Zusatzfelder EINES JTL-Lieferanten (EUR-Abrechnung, Kurzname, Logo, Notizen)
 * plus die Freigabeliste der Zahlungsziele - der einzige Schreibweg dieser Flaeche
 * (Task 11, Befund 15 - Option B, entschieden 2026-08-11).
 *
 * ============================== AUSGENOMMENE FLAECHE, KEIN NACHBAU ==============================
 *
 * Es gibt kein lauffaehiges Vorbild: `renderSuppliers()` (`index.html:13903`) schreibt in ein
 * `#page-suppliers`, das im Grundgeruest des Bestands nie existiert - jeder Aufruf wirft. Die
 * Wortlaut-/Differenzregel greift hier deshalb nicht (`test/visual/ausnahmen.ts`, Eintrag
 * `Suppliers`, Grund "neue-flaeche-ohne-vorbild"); abgesichert ist diese Datei ueber
 * `test/komponenten/Suppliers/verwaltung.test.tsx`, nicht ueber eine Baseline.
 *
 * ============================== DIE DREI GEMESSENEN NAHTSTELLEN ==============================
 *
 * 1. ZWEI SCHREIBWEISEN. Gelesen wird camelCase (`shortName`), geschrieben snake_case
 *    (`short_name`) - die vollstaendige Begruendung samt Fundstellen steht im Kopfkommentar von
 *    `api/lieferantenExtras.ts`. Diese Datei liest ausschliesslich ueber `feld.lesename` und
 *    schreibt ausschliesslich ueber `feld.schreibname`; keine dritte Liste, kein Literal.
 *
 * 2. `rowVersion` GEHOERT DEN EXTRAS, NICHT JTL. JTL-Stammdaten kommen live und sind nicht
 *    versioniert (`ladeLieferant`, `backend/src/domaene/lieferanten.ts:132-145`) - `rowVersion`
 *    stammt also immer aus der `supplier_extras`-Zeile. `null` ist ein gueltiger Wert und heisst
 *    "es gibt noch keine Zeile": der Server legt sie dann an, findet er doch eine, kommt ein 409
 *    statt einer stillen Ueberschreibung. Deshalb reist `rowVersion` UNVERAENDERT in den Koerper -
 *    auch als `null`.
 *
 * 3. ENTFALLEN am 01.10.2026 (Seite "Zahlungsvorlagen"): `payment_plan` (Raten | JSON, ohne Leser im
 *    Backend) und `seasonal_rules` (abgeschaltet). Die Spalten bleiben in der Datenbank; dieses
 *    Formular sendet sie nicht mehr und ueberschreibt bestehende Werte damit nicht.
 *
 * ============================== EIN RUECKSETZWEG (2026-08-13) ==============================
 *
 * Dieses Formular hat KEINEN Ruecksetz-Effekt. Sein ganzer Zustand entsteht beim Einhaengen, und
 * zurueckgesetzt wird es, indem der Aufrufer es ueber seine `key` neu einhaengt
 * (`LieferantenSeite.tsx`, `key={id-rowVersion}` - die ausfuehrliche Begruendung steht dort).
 *
 * WAS VORHER SCHIEFGING, vom Auftraggeber gemeldet ("wenn ich den Lieferanten oben wechsle, bleiben
 * die alten Werte einfach stehen"): es gab ZWEI Ruecksetzwege, und sie griffen in verschiedener
 * Reihenfolge. Ein `useEffect` auf `[kLieferant, rowVersion]` baute den Entwurf NACH der Zeichnung
 * neu; eine eigene `key` an `ZahlungsplanFeld` haengte die Ratenmaske WAEHREND der Zeichnung neu
 * ein. Die Maske las ihre Zeilen also aus dem Entwurf, der zu diesem Zeitpunkt noch dem VORIGEN
 * Lieferanten gehoerte - und las ihn nie wieder nach, denn ihre `key` hatte sich ja bereits
 * geaendert. Die Textfelder daneben stimmten, weil sie unmittelbar am Entwurf haengen; nur die
 * Maske blieb stehen. Wer danach eine Rate anfasste, schrieb den Plan des vorigen Lieferanten in
 * den Datensatz des neuen (`verwaltung.test.tsx`, "schreibt nach dem Wechsel den Plan des NEUEN
 * Lieferanten").
 *
 * Wer hier je wieder einen `useEffect` zum Zuruecksetzen einzieht, baut denselben Fehler nach: zwei
 * Wege, die dasselbe tun sollen, tun es frueher oder spaeter zu verschiedenen Zeitpunkten.
 *
 * ============================== RECHTE: GESPERRT, NICHT ABGEWIESEN ==============================
 *
 * Ohne `manage_suppliers` sind ALLE Bedienelemente `disabled` und tragen den Hinweis
 * `NUR_LESEN_TITEL` - die Entscheidung des Auftraggebers vom 2026-08-11 ("ausgegraut plus Tooltip"),
 * eine bewusste Abweichung vom Bestand, der bei den Lieferanten erst den Klick abgewiesen haette
 * (`index.html:13940`, `13949`, `13957`). In der Sprache von `state/rechteMatrix.ts` ist das
 * `art: "gesperrt"`, nicht `art: "handler"`; der Eintrag dazu steht in
 * `test/komponenten/rechte/matrix.test.tsx`.
 *
 * BENANNT, NICHT EIGENMAECHTIG GEAENDERT (Fix-Runde 1, 2026-08-11): `title` wird auf `disabled`-
 * Elementen in mehreren Browsern NICHT als Tooltip angezeigt - Chromium/Firefox unterdruecken die
 * Zeigergeste dort, weil ein deaktiviertes Element keine Zeigerereignisse empfaengt. Die Zusage
 * "deaktiviert MIT HINWEIS" haengt damit an einem Mechanismus, der ausgerechnet im gesperrten Fall
 * unzuverlaessig ist. Das ist BESTANDSPRAXIS und kein Regressionsbefund: der Drawer-Rechtedurchgang
 * (`Drawer.tsx`, `index.html:9296-9304`) setzt seit jeher genau dieses `title` auf genau diese
 * Elemente, und diese Flaeche folgt ihm bewusst statt einen zweiten Mechanismus zu erfinden.
 *
 * ENTSCHIEDEN am 2026-08-12: Der Auftraggeber hat den sichtbaren Satz beauftragt. Er steht jetzt als
 * `.supplier-extras-hinweis` ueber dem Formular; das `title` BLEIBT zusaetzlich - es ist richtig,
 * nur nicht ausreichend, und `verwaltung.test.tsx:470` prueft seinen Wortlaut an jedem Element.
 */
import { useEffect, useState, type JSX } from "react";
import { KonfliktFehler } from "../../api/basis.ts";
import { fehlertext } from "../../lib/fehlertext.ts";
import { SUPPLIER_EXTRAS_FELDER, extrasSchreiben } from "../../api/lieferantenExtras.ts";
import { ladeVorlagen } from "../../api/zahlungsvorlagen.ts";
import type { PaymentTemplateRow } from "../../api/zahlungsvorlagen.ts";
// Fix-Runde 1 (2026-08-11): aus `Drawer/drawerRechte.ts` statt aus `Drawer/Drawer.tsx`. Der Hinweis
// selbst ist unveraendert - nur die Naht ist schlanker: eine Konstantendatei ohne React, DOM und
// Drawer-Zustand, statt eines Formularmoduls, das das ganze Drawer-Modul hereinzieht, um an einen
// Satz zu kommen.
import { NUR_LESEN_TITEL } from "../Drawer/drawerRechte.ts";
import { PAYMENT_TEMPLATES } from "../../config/payments.ts";
import { Ikone } from "../Ikone.tsx";
import { ZahlungszieleFeld } from "./ZahlungszieleFeld.tsx";
import type { FreigabeZeile } from "../../api/lieferantenZahlungsziele.ts";

/** Wie ein Feld bedient wird. Der Name entscheidet zugleich ueber die Umwandlung beim Absenden. */
type Bedienart = "schalter" | "text" | "mehrzeilig";

interface Feldanzeige {
  schreibname: string;
  lesename: string;
  label: string;
  art: Bedienart;
}

/**
 * Beschriftung und Bedienart je Feld. Die NAMEN kommen aus `SUPPLIER_EXTRAS_FELDER` (also mittelbar
 * aus `backend/src/domaene/schreibbareFelder.ts`) - diese Tabelle ergaenzt sie nur um das, was das
 * Backend nicht weiss. Sie wird ABGELEITET und nicht abgeschrieben: faellt im Backend ein Feld weg
 * oder kommt eines hinzu, faellt hier der `Unbekanntes Feld`-Zweig auf, statt dass eine zweite Liste
 * still auseinanderlaeuft.
 */
const BESCHRIFTUNG: Readonly<Record<string, { label: string; art: Bedienart }>> = {
  eur: { label: "Abrechnung in EUR", art: "schalter" },
  short_name: { label: "Kurzname", art: "text" },
  // payment_template stand hier bis Migration 039 (Fixrunde 1, Zahlungsziele strukturiert,
  // 2026-09-07) als Auswahlmenue ("auswahl") mit eigenem Loeschknopf und "+ Neue Vorlage…"-Dialog -
  // die ganze Naht ist mit der Spalte entfallen, siehe Kopfkommentar von api/lieferantenExtras.ts.
  // Die Zuordnung Lieferant -> Vorlage bekommt ihre eigene Flaeche in einer spaeteren Aufgabe
  // (supplier_payment_terms, mehrere Optionen statt genau einer).
  logo: { label: "Logo (URL)", art: "text" },
  notes: { label: "Notizen", art: "mehrzeilig" },
};

const FELDER: readonly Feldanzeige[] = SUPPLIER_EXTRAS_FELDER.map((feld) => ({
  schreibname: feld.schreibname,
  lesename: feld.lesename,
  label: BESCHRIFTUNG[feld.schreibname]?.label ?? `Unbekanntes Feld: ${feld.schreibname}`,
  art: BESCHRIFTUNG[feld.schreibname]?.art ?? "text",
}));

export interface ExtrasFormularProps {
  /** JTL-Kennung als Zeichenkette - genau die `id`, die `lieferantLaden` liefert. */
  kLieferant: string;
  /**
   * Die Quelle der ANZEIGEWERTE, gelesen ueber `feld.lesename` (camelCase). `null`, wenn es fuer
   * diesen Lieferanten noch gar keinen Datensatz gibt.
   *
   * BERICHTIGT IN DER FIX-RUNDE 1: hier stand "die gelesene `supplier_extras`-Zeile". Uebergeben
   * wird tatsaechlich der GANZE flache Datensatz aus `entschachtele()` (`api/lieferanten.ts`), also
   * JTL-Stammdaten UND Zusatzfelder in EINEM Objekt. Das ist heute kollisionsfrei - die sechs
   * `lesename`-Schluessel kommen ausschliesslich aus der `supplier_extras`-Haelfte, `jtl` fuehrt
   * keinen davon (nachgerechnet in `api/lieferanten.ts`, Abschnitt KOLLISIONSFRAGE) - aber der
   * Vertrag muss beschreiben, was wirklich hereinkommt: kaeme in JTL je eine Spalte `logo` oder
   * `notes` dazu, entschiede ab dann die Reihenfolge im Spread, welcher Wert im Formular steht.
   */
  extras: Record<string, unknown> | null;
  /** Version der `supplier_extras`-ZEILE. `null` = die Zeile existiert (aus Sicht des Clients) nicht. */
  rowVersion: number | null;
  darfSchreiben: boolean;
  /** Bekommt die vom Server zurueckgegebene, frische Zeile - der Aufrufer haelt den Stand. */
  onGespeichert?: (zeile: Record<string, unknown>) => void;
  onFreigabenGespeichert?: (kLieferant: number, freigaben: FreigabeZeile[]) => void;
}

/** Der Anzeigewert eines Feldes aus der gelesenen Zeile - je Bedienart eine eigene Umformung. */
function ausZeile(feld: Feldanzeige, extras: Record<string, unknown> | null): string | boolean {
  const wert = extras?.[feld.lesename];
  if (feld.art === "schalter") return wert === true;
  return wert === null || wert === undefined ? "" : String(wert);
}

function entwurfAus(extras: Record<string, unknown> | null): Record<string, string | boolean> {
  const entwurf: Record<string, string | boolean> = {};
  for (const feld of FELDER) entwurf[feld.schreibname] = ausZeile(feld, extras);
  return entwurf;
}

export function ExtrasFormular({
  kLieferant,
  extras,
  rowVersion,
  darfSchreiben,
  onGespeichert,
  onFreigabenGespeichert,
}: ExtrasFormularProps): JSX.Element {
  /*
   * ALLER ZUSTAND DIESES FORMULARS ENTSTEHT BEIM EINHAENGEN - es gibt KEINEN Ruecksetz-Effekt mehr.
   * Zurueckgesetzt wird, indem der Aufrufer das ganze Bauteil ueber seine `key` neu einhaengt
   * (`LieferantenSeite.tsx`, `key={id-rowVersion}`); die Begruendung steht im Kopfkommentar oben
   * unter "EIN RUECKSETZWEG".
   */
  /**
   * Die geteilten Zahlungsvorlagen (Spec 2026-09-01, Abschnitt 3) - EINMAL beim Einhaengen geladen,
   * nicht bei jedem Lieferantenwechsel: die Liste ist lieferantenuebergreifend und aendert sich nicht
   * dadurch, welcher Lieferant gerade bearbeitet wird. `PAYMENT_TEMPLATES` bleibt als eingebauter
   * Fallback bestehen, falls das Laden fehlschlaegt (z. B. Netzwerkfehler) - ein leeres Dropdown waere
   * schlechter als die zehn urspruenglichen Vorlagen.
   *
   * Seit dem 01.10.2026 nur noch fuer `ZahlungszieleFeld` (Namen der Vorlagen, Auswahl der Freigaben):
   * die Saisonregeln sind entfallen, das direkte Zahlungsvorlage-Feld ist mit `payment_template`
   * (Migration 039) entfallen, siehe Kopfkommentar von `api/lieferantenExtras.ts`.
   */
  const [vorlagen, setVorlagen] = useState<PaymentTemplateRow[]>(
    () => PAYMENT_TEMPLATES.filter((v) => v.id !== "").map((v) => ({ id: v.id, label: v.label, rows: [...v.payments] })),
  );
  useEffect(() => {
    let abgebrochen = false;
    ladeVorlagen()
      .then((geladen) => {
        // Ganzzweig-Fix 1 (M1): eine erfolgreiche, aber falsch geformte Antwort (z.B. ein
        // Proxy-Fehlerobjekt statt eines Arrays) darf `vorlagen.map(...)` im Render nicht mit
        // einem geworfenen Fehler treffen - das riss sonst die GANZE Lieferantenseite mit, nicht
        // nur das Dropdown. Der bestehende `.catch` faengt nur eine Ablehnung des Promise ab,
        // kein erfolgreiches, aber falsch geformtes Ergebnis - deshalb die Pruefung hier.
        if (!abgebrochen && Array.isArray(geladen)) setVorlagen(geladen);
      })
      .catch(() => { // still: die Vorbelegung oben ist ein brauchbarer Ersatzwert fuer einen reinen Lesevorgang
        // Fallback (die Vorbelegung oben) bleibt einfach stehen - kein Fehlerhinweis fuer einen
        // Lesevorgang, der ohnehin einen brauchbaren Ersatzwert hat.
      });
    return () => {
      abgebrochen = true;
    };
  }, []);
  const [entwurf, setEntwurf] = useState<Record<string, string | boolean>>(() => entwurfAus(extras));
  const [fehler, setFehler] = useState<string | null>(null);
  const [konfliktStand, setKonfliktStand] = useState<unknown>(undefined);
  const [laeuft, setLaeuft] = useState(false);

  /**
   * Baut den `felder`-Koerper aus den vier Zusatzfeldern. `payment_plan` und `seasonal_rules` fehlen
   * absichtlich: sie werden nicht mehr gesendet, bestehende Werte bleiben in der Datenbank unberuehrt.
   */
  function felderAusEntwurf(): Record<string, unknown> {
    const felder: Record<string, unknown> = {};
    for (const feld of FELDER) {
      const wert = entwurf[feld.schreibname];
      if (feld.art === "schalter") {
        felder[feld.schreibname] = wert === true;
      } else {
        // "text" und "mehrzeilig" teilen sich diesen Zweig: beide fuehren im Entwurf eine
        // Zeichenkette, und der leere Fall bedeutet bei beiden dasselbe.
        const text = String(wert ?? "");
        // Leer heisst "nicht gepflegt" und wird als NULL geschrieben, nicht als leere Zeichenkette:
        // sonst unterschiede die Datenbank zwei Zustaende, die der Nutzer nicht unterscheiden kann.
        felder[feld.schreibname] = text === "" ? null : text;
      }
    }
    return felder;
  }

  async function speichern(): Promise<void> {
    // Verteidigung in der Tiefe: die Felder sind bereits gesperrt (siehe unten), der Rueckruf kann
    // aber ueber einen programmatischen Aufruf trotzdem hier ankommen.
    if (!darfSchreiben || laeuft) return;
    setFehler(null);
    setKonfliktStand(undefined);
    let felder: Record<string, unknown>;
    try {
      felder = felderAusEntwurf();
    } catch (grund) {
      setFehler(fehlertext(grund, "Die Eingaben konnten nicht gelesen werden."));
      return;
    }
    setLaeuft(true);
    try {
      const zeile = await extrasSchreiben(kLieferant, felder, rowVersion);
      onGespeichert?.(zeile);
    } catch (grund) {
      if (grund instanceof KonfliktFehler) {
        // Drei-Wege-Absage wie ueberall sonst: der Serverstand reist im 409 bereits mit
        // (`KonfliktFehler.aktuellerStand`), es braucht kein zweites Laden - und der Entwurf bleibt
        // unangetastet stehen.
        setKonfliktStand(grund.aktuellerStand);
        setFehler("Zwischenzeitlich fremd geaendert - nichts wurde gespeichert.");
      } else {
        // Inline bleibt inline (Spec R2): Text aus `fehlertext()`, der Satz zum Scheitern ist der Kontext.
        setFehler(fehlertext(grund, "Die Zusatzfelder konnten nicht gespeichert werden."));
      }
    } finally {
      setLaeuft(false);
    }
  }

  const sperrTitel = darfSchreiben ? undefined : NUR_LESEN_TITEL;

  return (
    <form
      className="supplier-extras"
      aria-label="Zusatzfelder des Lieferanten"
      onSubmit={(ereignis) => {
        ereignis.preventDefault();
        void speichern();
      }}
    >
      <div className="supplier-extras-kopf">
        <h3>Zusatzfelder des Tools</h3>
        <p>Nur in diesem Werkzeug — sie wirken nicht nach JTL zurück.</p>
      </div>
      {!darfSchreiben && (
        <p className="supplier-extras-hinweis" role="status">
          <Ikone name="triangle-alert" />
          {NUR_LESEN_TITEL}
        </p>
      )}
      {FELDER.map((feld) => {
        const kennung = `extras-${feld.schreibname}`;
        const wert = entwurf[feld.schreibname];
        return (
          <div className="field" key={feld.schreibname}>
            <label htmlFor={kennung}>{feld.label}</label>
            {feld.art === "schalter" ? (
              <input
                id={kennung}
                type="checkbox"
                checked={wert === true}
                disabled={!darfSchreiben}
                title={sperrTitel}
                onChange={(ereignis) =>
                  setEntwurf((alt) => ({ ...alt, [feld.schreibname]: ereignis.target.checked }))
                }
              />
            ) : feld.art === "text" ? (
              <>
                <input
                  id={kennung}
                  type="text"
                  value={String(wert ?? "")}
                  disabled={!darfSchreiben}
                  title={sperrTitel}
                  onChange={(ereignis) =>
                    setEntwurf((alt) => ({ ...alt, [feld.schreibname]: ereignis.target.value }))
                  }
                />
                {feld.schreibname === "logo" && String(wert ?? "").trim() !== "" ? (
                  <span className="supplier-logo-vorschau">
                    <img className="supplier-logo sm" src={String(wert)} alt="" />
                    Vorschau
                  </span>
                ) : null}
              </>
            ) : (
              // Einziger verbleibender Zweig: "mehrzeilig".
              <textarea
                id={kennung}
                rows={3}
                value={String(wert ?? "")}
                disabled={!darfSchreiben}
                title={sperrTitel}
                onChange={(ereignis) =>
                  setEntwurf((alt) => ({ ...alt, [feld.schreibname]: ereignis.target.value }))
                }
              />
            )}
          </div>
        );
      })}
      <div className="field supplier-zahlungsziele">
        <label id="zahlungsziele-label">Freigegebene Zahlungsziele</label>
        <ZahlungszieleFeld
          kLieferant={Number(kLieferant)}
          darfSchreiben={darfSchreiben}
          vorlagen={vorlagen}
          onFreigabenGespeichert={onFreigabenGespeichert}
          onVorlageAngelegt={(vorlage) => setVorlagen((alt) => [...alt, vorlage])}
        />
      </div>
      {/*
        Die `<datalist id="extras-vorlagen">` steht hier nicht mehr: das Zahlungsvorlage-Feld, das
        sie einst bediente, ist mit Migration 039 (Fixrunde 1) ganz entfallen. Eine zurueckgelassene
        Liste ohne `list=`-Verweis waere toter Ballast - dieselbe Klasse Fund, die `.suppliers-treffer`
        aus `suchfeld.css` hat verschwinden lassen.
      */}
      <div className="supplier-extras-foot">
        <button type="submit" className="btn-primary" disabled={!darfSchreiben || laeuft} title={sperrTitel}>
          {/*
            KEINE Beschriftungsaenderung, und das ist Absicht: mehrere Faelle greifen den Knopf ueber
            `getByRole("button", { name: "Extras speichern" })`. Ein Text, der waehrend des Laufs
            wechselt, machte diese Abfrage vom Zeitpunkt abhaengig - der Punkt steht deshalb VOR der
            Beschriftung und ist `aria-hidden`, damit er den zugaenglichen Namen nicht veraendert.
          */}
          {laeuft ? <span className="supplier-extras-laedt" aria-hidden="true" /> : null}
          Extras speichern
        </button>
      </div>
      {fehler !== null ? (
        <p className="supplier-extras-fehler" role="alert">
          {fehler}
        </p>
      ) : null}
      {konfliktStand !== undefined ? (
        <div className="supplier-extras-konflikt">
          <strong>Serverstand</strong>
          <table>
            <thead>
              <tr>
                <th scope="col">Feld</th>
                <th scope="col">Ihr Wert</th>
                <th scope="col">Serverstand</th>
              </tr>
            </thead>
            <tbody>
              {FELDER.map((feld) => {
                const roh = (konfliktStand as Record<string, unknown> | null)?.[feld.lesename];
                const serverFehlt = roh === null || roh === undefined;
                let meinerText: string;
                let seinerText: string;
                let gleich: boolean;
                if (feld.art === "schalter") {
                  // Derselbe Befund wie einst bei "Aktiv" (rohes "Y" statt "ja"): eine Tabelle, deren
                  // Daseinszweck Lesbarkeit ist, darf kein rohes `true`/`false` zeigen. Der Vergleich
                  // laeuft ueber den BOOLESCHEN Wert, nicht ueber den Anzeigetext - sonst verglichen
                  // wir "ja"/"nein" (Anzeige) gegen "true"/"false" (aus `ausZeile`) und jede gleiche
                  // Zeile saehe faelschlich abweichend aus.
                  const meinBool = entwurf[feld.schreibname] === true;
                  const seinBool = roh === true;
                  meinerText = meinBool ? "ja" : "nein";
                  seinerText = serverFehlt ? "–" : seinBool ? "ja" : "nein";
                  gleich = meinBool === seinBool;
                } else {
                  // Reiner Zeichenkettenvergleich: die vier verbleibenden Felder sind Text bzw. Schalter.
                  const meiner = String(entwurf[feld.schreibname] ?? "");
                  const seiner = ausZeile(feld, (konfliktStand ?? null) as Record<string, unknown> | null);
                  meinerText = meiner === "" ? "–" : meiner;
                  seinerText = serverFehlt ? "–" : String(seiner);
                  gleich = meiner === String(seiner);
                }
                return (
                  <tr key={feld.schreibname} className={gleich ? undefined : "abweichend"}>
                    <td>{feld.label}</td>
                    <td>{meinerText}</td>
                    <td>{seinerText}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {/*
            Das rohe JSON BLEIBT - eingeklappt, nicht entfernt. Die 409-Antwort traegt die ganze
            Zeile einschliesslich `rowVersion`, `updatedAt` und `updatedBy`, und die Tabelle darueber
            zeigt nur die sechs pflegbaren Felder. `<summary>` ist kein Bedienelement im Sinne der
            Rechtematrix.
          */}
          <details>
            <summary>Rohdaten anzeigen</summary>
            <pre className="mono">{JSON.stringify(konfliktStand, null, 2)}</pre>
          </details>
        </div>
      ) : null}
    </form>
  );
}
